import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Public Pages
import Home from './pages/Home';
import Login from './pages/Login';

// Client Pages
import ClientDashboard from './pages/client/Dashboard';
import PersonalData from './pages/client/PersonalData';
import Documents from './pages/client/Documents';
import Insurance from './pages/client/Insurance';
import FinanceProject from './pages/client/FinanceProject';
import Preferences from './pages/client/Preferences';
import MesJustificatifs from './pages/client/MesJustificatifs';
import LoanPreparation from './pages/client/LoanPreparation';
import MTrustIntro from './pages/client/MTrustIntro';
import ImpotsLogin from './pages/client/ImpotsLogin';
import ShareData from './pages/client/ShareData';
import BankConnection from './pages/client/BankConnection';
import BankSelection from './pages/client/BankSelection';
import BankLogin from './pages/client/BankLogin';
import LoanProcessing from './pages/client/LoanProcessing';
import LoanSuccess from './pages/client/LoanSuccess';
import IdentityUpload from './pages/client/IdentityUpload';
import JustificatifSuccess from './pages/client/JustificatifSuccess';
import RIBUpload from './pages/client/RIBUpload';
import DomicileUpload from './pages/client/DomicileUpload';
import BulletinSalaireUpload from './pages/client/BulletinSalaireUpload';
import HelpContact from './pages/client/HelpContact';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import ClientsManagement from './pages/admin/ClientsManagement';
import FundsManagement from './pages/admin/FundsManagement';
import AdminJustificatifs from './pages/admin/AdminJustificatifs';
import AdminClientData from './pages/admin/AdminClientData';
import CreditRequests from './pages/admin/CreditRequests';
import AdminMessages from './pages/admin/AdminMessages';

// Simulation Pages
import SimulationStep1 from './pages/simulation/SimulationStep1';
import SimulationStep2 from './pages/simulation/SimulationStep2';
import SimulationStep3 from './pages/simulation/SimulationStep3';

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/client/dashboard" replace />;
  }

  if (!requireAdmin && user.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<Login />} />
      
      {/* Simulation Routes (Public) */}
      <Route path="/simulation/step1" element={<SimulationStep1 />} />
      <Route path="/simulation/step2" element={<SimulationStep2 />} />
      <Route path="/simulation/step3" element={<SimulationStep3 />} />

      {/* Client Routes */}
      <Route
        path="/client/dashboard"
        element={
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/personal-data"
        element={
          <ProtectedRoute>
            <PersonalData />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/documents"
        element={
          <ProtectedRoute>
            <Documents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/insurance"
        element={
          <ProtectedRoute>
            <Insurance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/finance"
        element={
          <ProtectedRoute>
            <FinanceProject />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/preferences"
        element={
          <ProtectedRoute>
            <Preferences />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/justificatifs"
        element={
          <ProtectedRoute>
            <MesJustificatifs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/loan-flow/preparation"
        element={
          <ProtectedRoute>
            <LoanPreparation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/loan-flow/mtrust-intro"
        element={
          <ProtectedRoute>
            <MTrustIntro />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/loan-flow/impots-login"
        element={
          <ProtectedRoute>
            <ImpotsLogin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/loan-flow/share-data"
        element={
          <ProtectedRoute>
            <ShareData />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/loan-flow/bank-connection"
        element={
          <ProtectedRoute>
            <BankConnection />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/loan-flow/bank-selection"
        element={
          <ProtectedRoute>
            <BankSelection />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/loan-flow/bank-login/:bankId"
        element={
          <ProtectedRoute>
            <BankLogin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/loan-flow/processing"
        element={
          <ProtectedRoute>
            <LoanProcessing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/loan-flow/success"
        element={
          <ProtectedRoute>
            <LoanSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/identity-upload"
        element={
          <ProtectedRoute>
            <IdentityUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/rib-upload"
        element={
          <ProtectedRoute>
            <RIBUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/domicile-upload"
        element={
          <ProtectedRoute>
            <DomicileUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/bulletin-salaire-upload"
        element={
          <ProtectedRoute>
            <BulletinSalaireUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/justificatif-success"
        element={
          <ProtectedRoute>
            <JustificatifSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/mes-justificatifs"
        element={
          <ProtectedRoute>
            <MesJustificatifs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/help-contact"
        element={
          <ProtectedRoute>
            <HelpContact />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/messages"
        element={
          <ProtectedRoute requireAdmin>
            <AdminMessages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute requireAdmin>
            <ClientsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/funds"
        element={
          <ProtectedRoute requireAdmin>
            <FundsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/justificatifs"
        element={
          <ProtectedRoute requireAdmin>
            <AdminJustificatifs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/client-data"
        element={
          <ProtectedRoute requireAdmin>
            <AdminClientData />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/credit-requests"
        element={
          <ProtectedRoute requireAdmin>
            <CreditRequests />
          </ProtectedRoute>
        }
      />

      {/* Redirect unknown routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
